/**
 * Utility to clear stuck sync notifications
 * This can be used to clear any lingering sync notifications that may appear
 * after failed image update jobs
 */
import eventBus from './eventBus';
import { db } from './db';

/**
 * Clears stuck sync notification and optionally cleans up orphaned image jobs
 * @param {boolean} cleanupJobs - Whether to also clean up failed/stuck image jobs
 */
export async function clearSyncNotification(cleanupJobs = false) {
    console.log('Clearing stuck sync notification...');
    
    // Emit event to clear the notification
    eventBus.emit('clear-sync-notification');
    
    if (cleanupJobs) {
        console.log('Cleaning up orphaned image jobs...');
        
        try {
            // Find and remove stuck image jobs (failed image-s3-update jobs without parent entities)
            const imageJobs = await db.jobs
                .where('type')
                .anyOf(['image-s3-upload', 'image-s3-update', 'image-confirm'])
                .toArray();
            
            let removedCount = 0;
            
            for (const job of imageJobs) {
                // Check if this is an update job
                if (job.type === 'image-s3-update' && job.attempt > 3) {
                    // Remove jobs that have failed more than 3 times
                    await db.jobs.delete(job.uuid);
                    removedCount++;
                    console.log(`Removed failed job ${job.uuid} (type: ${job.type})`);
                }
            }
            
            // Also reset any stuck images with pending/uploading status but no associated jobs
            const stuckImages = await db.images
                .where('upload_status')
                .anyOf(['pending', 'uploading'])
                .toArray();
            
            const activeJobImageUuids = new Set(
                imageJobs.map(job => job.payload?.image_uuid || job.payload?.existing_image_uuid)
                    .filter(Boolean)
            );
            
            for (const image of stuckImages) {
                if (!activeJobImageUuids.has(image.uuid)) {
                    // Image is stuck with no active job, reset its status
                    await db.images.update(image.uuid, {
                        upload_status: 'failed',
                        updated_at: new Date().toISOString()
                    });
                    console.log(`Reset stuck image ${image.uuid} status to 'failed'`);
                }
            }
            
            console.log(`Cleanup complete. Removed ${removedCount} failed jobs.`);
        } catch (error) {
            console.error('Error during cleanup:', error);
        }
    }
    
    console.log('Sync notification cleared');
}

// Export for use in development console
if (typeof window !== 'undefined') {
    window.clearSyncNotification = clearSyncNotification;
}

export default clearSyncNotification;